'use client';

import { useRouter } from '@/navigation';
import { useLocale } from 'next-intl';
import { useEffect, useTransition } from 'react';
import { useAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { usePathname } from '@/navigation';

import { galaxyTabItems, marsTabItems, TabItem } from '../../shared/data';
import { galaxySidebarSwitchTabIndexAtom, marsSidebarSwitchTabIndexAtom } from '@/atoms/io';
import { MaybeMatch } from '@nf/utils-common/fpw/monadic';
import { useFirstRender } from '@nf/utils-common/hooks';
import { useSportHref } from '@/hooks/menu/use-sport-href';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';
import { TaskTypeEnum } from '@/domains/wc-promotion/types';
import { useSiteStore } from '@/store/site';
import { GalaxySidebarSwitchTabIndex, MarsSidebarSwitchTabIndex } from '@/types/nav';
import { KafkaNote } from '@nf/utils-common/constants';
import { wcPromotionMissionService } from '@/domains/wc-promotion/services/wc-promotion-mission.service';
import { useOddsPageSwitcher } from '@/hooks/use-odds-page-switcher';
import { sidebarStyle } from '../../sidebar.style';
import clsx from 'clsx';

const matchPath = (pathname: string) => (item: TabItem) =>
	pathname.startsWith(item.href);

export const SidebarSportsSwitch = () => {
	const router = useRouter();
	const locale = useLocale();
	const pathname = usePathname();
	const [isPending, startTransition] = useTransition();
	const isFirstRender = useFirstRender();
	const view = useAppearanceStore(state => state.view);
	const [tabIndex, setTabIndex] = useAtom(view === ViewEnum.galaxy ? galaxySidebarSwitchTabIndexAtom : marsSidebarSwitchTabIndexAtom);
	const t = useTranslations('General');
	const { createSportHref } = useSportHref();
	const tabItems = view === ViewEnum.galaxy ? galaxyTabItems : marsTabItems;
	const [isParlayMode, setCurrentPage] = useSiteStore(state => [state.isParlayMode, state.setCurrentPage]);
	const { disableParlay, switchToParlay } = useOddsPageSwitcher();

	const findTabIndexByPathname = () => {
		const currentTabIndex = tabItems.findIndex(matchPath(pathname));
		return currentTabIndex > -1 ? currentTabIndex : 0;
	};

	const switchTabType = (index: number) => () => {
		setTabIndex(index);
		const tabItem = tabItems[index];
		if (view === ViewEnum.galaxy || view === ViewEnum.mars) {
			setKafka(tabItem);
		}
		const redirectPath = createSportHref(tabItem.href);

		if (view === ViewEnum.mars && index === MarsSidebarSwitchTabIndex.Parlay) {
			wcPromotionMissionService.notifyMission({
				taskType: TaskTypeEnum.ClickSabaFeatures,
				productCode: 'parlay'
			});
			switchToParlay();
			setCurrentPage('lbl_mixparlay');
		} else {
			disableParlay();
			setCurrentPage('lbl_Sports');
		}
		startTransition(() => {
			router.push(redirectPath, { locale });
		});
	};

	const setKafka = (tabItem: TabItem) => {
		const isGalaxy = view === ViewEnum.galaxy;
		const action = isGalaxy
			? (tabItem.href === '/live' ? 9 : 10) // 9: live, 10: sports
			: (tabItem.tabIndex === MarsSidebarSwitchTabIndex.Sports ? 10 : 11); // 10: sports, 11: parlay
		const kafkaData = {
			note: isGalaxy ? KafkaNote.GalaxyV2 : KafkaNote.MarsV1,
			type: 4,
			action
		}
		setKafkaNormal(kafkaData);
	}

	useEffect(() => {
		MaybeMatch(isFirstRender)
			.fold(
				() => { },
				() => {
					const tabIndexByPathname = findTabIndexByPathname();

					if (tabIndex !== tabIndexByPathname) {
						setTabIndex(tabIndexByPathname);
					}
				}
			);
	}, [isFirstRender, pathname, tabIndex]);
	useEffect(() => {
		if (view === ViewEnum.mars) {
			if (!isParlayMode) {
				setTabIndex(MarsSidebarSwitchTabIndex.Sports);
			} else {
				setTabIndex(MarsSidebarSwitchTabIndex.Parlay);
			}
		} else {
			const tabIndexByPathname = findTabIndexByPathname();
			const isVirtualSportsPath = /(^|\/)sports\/(116|134)(\/|$)/.test(pathname); // betby virtual sports path
			if (isVirtualSportsPath) {
				return;
			}
			if (tabIndex !== tabIndexByPathname) {
				setTabIndex(tabIndexByPathname);
			}
		}
	}, [isParlayMode, pathname, tabIndex]);
	return (
		<div
			className={clsx('live-switch', { [sidebarStyle.liveSwitch]: view === ViewEnum.mars })}
			data-selected={tabIndex}
		>
			{tabItems.map(item => (
				<div
					key={item.tabIndex}
					className={clsx('live-switch__btn', { [sidebarStyle.liveSwitchBtn]: view === ViewEnum.mars })}
					data-selected={item.tabIndex === tabIndex}
					onClick={switchTabType(item.tabIndex)}
				>
					<span className="live-switch__text">
						{t(item.title)}
					</span>
				</div>
			))}
		</div>
	);
};